import { CommonModule, DatePipe } from "@angular/common";
import {
  ChangeDetectorRef,
  Component,
  computed,
  inject,
  OnDestroy,
  signal,
  ViewChild,
  ViewEncapsulation,
} from "@angular/core";
import { FormBuilder, FormGroup, ReactiveFormsModule } from "@angular/forms";
import {
  MatOptionModule,
  provideNativeDateAdapter,
} from "@angular/material/core";
import { ActivatedRoute, NavigationStart, Router } from "@angular/router";
import { TranslateModule, TranslateService } from "@ngx-translate/core";
import { TablerIconsModule } from "angular-tabler-icons";
import { TourStoreLinkService } from "src/app/core/tours-cdk/toursservices/tour-store-link.service";

import {
  catchError,
  EMPTY,
  filter,
  finalize,
  forkJoin,
  Observable,
  of,
  Subject,
  switchMap,
  takeUntil,
  tap,
} from "rxjs";
import { MaterialModule } from "src/app/material.module";
import { ProfileService } from "src/app/pages/profile/profile.service";
import {
  getOnboardingStore,
  OnboardingStage,
  getOnboardingStage,
} from "src/app/core/guards/first-time-user-experience.helper";
import {
  isUserOnboardingComplete,
  isUserOnboardingInProgress,
} from "src/app/pages/profile/profile.model";
import { ProviderCaseStatusService } from "src/app/services/apps/providers/provider-case-status.service";
import { ProviderClaimTypesService } from "src/app/services/apps/providers/provider-claim-types.service";
import { ProviderService } from "src/app/services/apps/providers/provider.service";
import { StoreService } from "src/app/services/apps/store/store.service";
import {
  StoreProviderFilterHelper,
  StoreProviderFilterOptions,
  StoreProviderFilterState,
} from "src/app/shared/helpers/store-provider-filter.helper";
import {
  AlternativeContentActionConfig,
  AlternativeContentActionChildConfig,
  AlternativeContentComponent,
  AlternativeContentConfig,
} from "src/app/shared/components/alternative-content";
import { DateRangeSelectorComponent } from "src/app/shared/components/date-range-selector/date-range-selector.component";
import { LoaderService } from "src/app/shared/components/loader/loader.service";
import { ColCountDirective } from "src/app/shared/directives/col-count.directive";
import { ColSpanDirective } from "src/app/shared/directives/col-span.directive";
import { TimeUtil } from "src/app/shared/services/time-util.service";
import {
  eventAction,
  ViewContextHistoryStateService,
} from "src/app/shared/services/view-context-history-state.service";
import {
  describeApiRequestFailed,
  describeFilterApplied,
  describeListLoaded,
  describeViewReady,
  extractSupportErrorMessage,
  resolveSupportContextError,
  type SupportHistoryRecordOptions,
} from "src/app/shared/helpers/contact-support-history.helper";
import {
  recordApiRequestFailed,
  recordApiRequestStarted,
  recordApiRequestSucceeded,
} from "src/app/shared/helpers/support-api-history.helper";
import { RouteStore } from "src/app/shared/stores/route-store";
import { ORNAMENT_ASSETS } from "src/app/shared/constants/ornament-assets";
import { OnboardingFlowService } from "../onboarding/onboarding-flow.service";
import { SharedWithMeComponent } from "../store-management/stores/shared-with-me/shared-with-me.component";

import { OnboardingModalService } from "../onboarding/onboarding-modal.service";
import {
  ProviderCaseStatusGetDTO,
  ProviderGetDTO,
} from "../providers/provider.model";
import { StoreGetDTO } from "../store-management/stores/store";
import {
  buildDashboardWidgetKpis,
  createEmptyDashboardWidgetKpis,
} from "./helpers/dashboard-kpi.mapper";
import { DashboardListNavigationContext } from "./models/dashboard-list-navigation.model";
import { DashboardFilter, toDashboardApiQueryParams } from "./models/filter.model";
import { DashboardWidgetKpis } from "./models/dashboard-widget-kpi.model";
import { FiledClaimUiModel } from "./models/filed-case-ui.model";
import { UnClaimUiModel } from "./models/unclaim-cases-ui.model";
import { DashboardUsageWidgetComponent } from "./dashboard-usage-widget/dashboard-usage-widget.component";
import { BillingServiceOverviewStore } from "../billing/billing-status-widget/billing-service-overview.store";
import {
  CaseOverviewWidgetComponent,
  CaseStatusWidgetComponent,
  LostOutboundClaimWidgetComponent,
  OpportunityValueWidgetComponent,
  OtherClaimsWidgetComponent,
  StoreProviderReportWidgetComponent,
  DashboardStoreConfigStatusWidgetComponent,
  DashboardReimbursementSummaryWidgetComponent,
} from "./widgets";
import { DashboardService } from "./dashboard.service";
import { BillingServiceOverview } from "../billing/billing-status-widget/billing-service-overview.model";
import {
  DashboardFilterFormValue,
} from "./dashboard-view-state.cache";
import { DashboardContextData } from "./dashboard.resolver";

interface DashboardViewModelState {
  filedClaim: FiledClaimUiModel;
  unfilledClaim: UnClaimUiModel;
  providers: ProviderGetDTO[];
  stores: StoreGetDTO[];
  claimTypes: any;
  caseStatuses: ProviderCaseStatusGetDTO[];
  usageOverview?: BillingServiceOverview | null;
  usageOverviewError?: string | null;
  filtersApplied: DashboardFilter;
  paramsToCallDashboardApi?: DashboardFilter;
}

@Component({
  selector: "app-dashboard",
  templateUrl: "./dashboard.component.html",
  imports: [
    MaterialModule,
    ReactiveFormsModule,
    TranslateModule,
    TablerIconsModule,
    MatOptionModule,
    CommonModule,
    ColCountDirective,
    ColSpanDirective,
    DateRangeSelectorComponent,
    AlternativeContentComponent,
    CaseOverviewWidgetComponent,
    CaseStatusWidgetComponent,
    OpportunityValueWidgetComponent,
    OtherClaimsWidgetComponent,
    LostOutboundClaimWidgetComponent,
    StoreProviderReportWidgetComponent,
    DashboardUsageWidgetComponent,
    DashboardStoreConfigStatusWidgetComponent,
    DashboardReimbursementSummaryWidgetComponent,
    SharedWithMeComponent,
  ],
  styleUrls: ["./dashboard.component.scss"],
  encapsulation: ViewEncapsulation.None,
  providers: [provideNativeDateAdapter(), DatePipe],
})
export class DashboardComponent implements OnDestroy {
  readonly datePipe = inject(DatePipe);
  readonly cdr = inject(ChangeDetectorRef);
  readonly route = inject(ActivatedRoute);
  readonly router = inject(Router);
  readonly routeStore = inject(RouteStore);
  readonly tourStoreLinkService = inject(TourStoreLinkService);
  readonly fb = inject(FormBuilder);
  readonly dashboardSvc = inject(DashboardService);
  readonly providerSvc = inject(ProviderService);
  readonly storeSvc = inject(StoreService);
  readonly loaderService = inject(LoaderService);
  readonly providerClaimTypeSvc = inject(ProviderClaimTypesService);
  readonly providerCaseStatusSvc = inject(ProviderCaseStatusService);
  readonly billingOverviewStore = inject(BillingServiceOverviewStore);
  readonly timeUtil = inject(TimeUtil);
  readonly translate = inject(TranslateService);
  readonly maxDate = new Date();
  @ViewChild("storeProviderReportWidget")
  storeProviderReportWidget?: StoreProviderReportWidgetComponent;
  readonly rangeDateOptions: {
    displayOption: string;
    value: { startDate: string; endDate: string };
  }[] = [
    {
      displayOption: "Last 15 Months",
      value: this.timeUtil.getLastMonthsDateRange(15),
    },
    {
      displayOption: "Last 12 Months",
      value: this.timeUtil.getLastMonthsDateRange(12),
    },
    {
      displayOption: "Last 9 Months",
      value: this.timeUtil.getLastMonthsDateRange(9),
    },
    {
      displayOption: "Last 6 Months",
      value: this.timeUtil.getLastMonthsDateRange(6),
    },
    {
      displayOption: "Last 3 Months",
      value: this.timeUtil.getLastMonthsDateRange(3),
    },
    {
      displayOption: "Last Month",
      value: this.timeUtil.getLastMonthsDateRange(1),
    },
  ];
  readonly profileService = inject(ProfileService);
  readonly initailRange = this.timeUtil.getLastMonthsDateRange(12);
  private readonly storeProviderFilterOptions: StoreProviderFilterOptions = {
    allowAllProviders: true,
  };
  readonly filterFormInitialStateDefault: DashboardFilterFormValue = {
    provider: "",
    store: "",
    startDate: this.initailRange.startDate,
    endDate: this.initailRange.endDate,
  };
  isFilterModified = signal(false);
  filterForm: FormGroup;
  private readonly destroy$ = new Subject<void>();
  filedClaim = this.route.snapshot.data["dashboardData"]
    ?.filedClaim as FiledClaimUiModel;
  unfilledClaim = this.route.snapshot.data["dashboardData"]
    ?.unfiledClaim as UnClaimUiModel;

  providers: ProviderGetDTO[] =
    this.route.snapshot.data["dashboardData"]?.providers ?? [];
  stores = Array.isArray(this.route.snapshot.data["dashboardData"]?.stores)
    ? (this.route.snapshot.data["dashboardData"]?.stores as StoreGetDTO[])
    : [];
  providerOptions: ProviderGetDTO[] = [];
  filteredStores: StoreGetDTO[] = [];
  showStoreFilter = true;
  showProviderFilter = true;
  claimTypes = this.route.snapshot.data["dashboardData"]?.claimTypes;

  caseStatuses: ProviderCaseStatusGetDTO[] =
    this.route.snapshot.data["dashboardData"]?.caseStatuses;
  error = signal<{ message: string } | null>(
    this.route.snapshot.data["dashboardData"]?.error || null,
  );

  labelLastNumberMonths = "Last 12 Months";
  currentRangeKey = "Last 12 Months";
  widgetKpis: DashboardWidgetKpis = createEmptyDashboardWidgetKpis(
    this.labelLastNumberMonths,
    this.caseStatuses ?? [],
  );

  get onlyOneProvider(): boolean {
    return StoreProviderFilterHelper.onlyOneProvider(this.providerOptions);
  }

  get singleProviderName(): string {
    return StoreProviderFilterHelper.resolveProviderDisplayName(
      this.providerOptions,
      this.filterForm?.value?.provider,
    );
  }

  get onlyOneStore(): boolean {
    return StoreProviderFilterHelper.onlyOneStoreForProvider(
      this.filteredStores,
    );
  }

  get singleStoreName(): string {
    return StoreProviderFilterHelper.resolveStoreDisplayName(
      this.filteredStores,
      this.filterForm?.value?.store ?? "",
    );
  }

  /** Returns true when the user has stores but all of them are delegated (billed by the owner). */
  get allStoresDelegated(): boolean {
    return this.stores.length > 0 && this.stores.every((store) => store.isBillingDelegated);
  }

  /** Returns true when every store has incomplete onboarding. */
  get allStoresIncomplete(): boolean {
    return (
      this.stores.length > 0 &&
      this.stores.every((store) => !store.onboarding?.isComplete)
    );
  }

  get isOnboardingComplete(): boolean {
    return !isUserOnboardingInProgress(this.profileService.userProfile());
  }

  /** Returns true when there are no stores and onboarding is complete. */
  get noStoresButOnboardingComplete(): boolean {
    return this.stores.length === 0 && this.isOnboardingComplete;
  }

  get completedStores(): StoreGetDTO[] {
    return StoreProviderFilterHelper.getCompletedStores(this.stores);
  }

  get listNavigationContext(): DashboardListNavigationContext {
    return {
      provider: this.filterForm?.value?.provider ?? "",
      storeId: this.filterForm?.value?.store ?? "",
      startDate: this.filterForm?.value?.startDate ?? "",
      endDate: this.filterForm?.value?.endDate ?? "",
    };
  }

  viewContextHistorySvc: ViewContextHistoryStateService<DashboardViewModelState> =
    inject(ViewContextHistoryStateService);

  onboardingModalService = inject(OnboardingModalService);
  private readonly onboardingFlow = inject(OnboardingFlowService);
  readonly pendingInvitationsCount = signal(0);
  readonly dashboardAlternativeContentConfig = computed(() =>
    this.buildDashboardAlternativeContentConfig(),
  );
  storesIncompleteAlternativeContentConfig: AlternativeContentConfig =
    this.buildStoresIncompleteAlternativeContentConfig();
  noStoresButOnboardingCompleteAlternativeContentConfig: AlternativeContentConfig =
    this.buildNoStoresButOnboardingCompleteAlternativeContentConfig();
  storesCompleteButOnboardingIncompleteAlternativeContentConfig: AlternativeContentConfig =
    this.buildStoresCompleteButOnboardingIncompleteAlternativeContentConfig();

  recordSupportHistory(
    eventAction: eventAction,
    eventDescription: string,
    options?: SupportHistoryRecordOptions & { filterParams?: DashboardFilter },
  ): void {
    this.viewContextHistorySvc.createContext({
      titleView: "Dashboard",
      eventAction,
      eventDescription,
      thisState: {
        filedClaim: this.filedClaim,
        unfilledClaim: this.unfilledClaim,
        providers: this.providers,
        stores: this.stores,
        claimTypes: this.claimTypes,
        caseStatuses: this.caseStatuses,
        usageOverview: this.billingOverviewStore.overview(),
        usageOverviewError: this.billingOverviewStore.errorMessage(),
        filtersApplied: this.filterForm.value,
        ...(options?.filterParams
          ? { paramsToCallDashboardApi: options.filterParams }
          : {}),
      },
      route: this.route.snapshot,
      error: resolveSupportContextError(eventAction, options?.error),
      setAutomaticallyScreenshot: false,
    });
  }

  ngOnInit() {
    this.routeStore.createContextFromActivatedRoute(this.route, this.router);
    this.maybeOpenDashboardOnboardingModal();
    this.setupNavigationPersistence();

    const routeContext = this.resolveDashboardRouteContext();

    const { store, provider } = this.buildInitialFilterState();
    const initialFilter: DashboardFilterFormValue =
      routeContext?.filterForm ?? {
        provider,
        store,
        startDate: this.initailRange.startDate,
        endDate: this.initailRange.endDate,
      };

    this.filterForm = this.fb.group({
      provider: [initialFilter.provider],
      store: [initialFilter.store],
      startDate: [initialFilter.startDate],
      endDate: [initialFilter.endDate],
    });
    this.syncWidgetKpis();
    this.syncProviderStoreFilters();

    this.filterForm.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.updateFilterModifiedState();
        this.onFilterSubmit();
      });

    this.filterForm
      .get("provider")
      ?.valueChanges.pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.syncProviderStoreFilters();
      });

    this.setRangeLabelByPeriod(
      this.filterForm.value.startDate,
      this.filterForm.value.endDate,
    );
    this.updateFilterModifiedState();

    // Update date range label when language changes
    this.translate.onLangChange
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.setRangeLabelByPeriod(
          this.filterForm.value.startDate,
          this.filterForm.value.endDate,
        );
      });
  }

  ngOnDestroy(): void {
    this.persistDashboardRouteState();
    this.destroy$.next();
    this.destroy$.complete();
  }

  /** Where the user is in onboarding. */
  get onboardingStage(): OnboardingStage {
    return getOnboardingStage(
      this.profileService.userProfile(),
      this.stores,
    );
  }

  private maybeOpenDashboardOnboardingModal(): void {
    const stage = this.onboardingStage;

    switch (stage) {
      case "link-first-store": {
        if (this.onboardingModalService.hasSeenWelcomeFirstTimeOnboarding()) {
          return;
        }
        this.onboardingFlow
          .openMoment("welcomeFirstTimeOnboarding", this.route.snapshot)
          .afterClosed()
          .subscribe((result) => {
            if (result) {
              this.tourStoreLinkService.start();
            }
          });
        return;
      }
      case "finish-store-setup": {
        if (this.onboardingModalService.hasSeenResumeIncompleteStoreSetup()) {
          return;
        }
        this.onboardingFlow.openMoment(
          "resumeIncompleteStoreSetup",
          this.route.snapshot,
        );
        return;
      }
      // Session-only gate, same pattern as the "resume incomplete store setup"
      // prompt above: reappears on a fresh reload until onboarding is done —
      // there's no durable "already saw this" flag anymore (the backend starts
      // the potential-cases job itself at store-link time now).
      case "candidate-claims":
      case "delegated-ready": {
        if (
          this.onboardingModalService.hasSeenAfterCompleteDeliveryAndThresholdForm()
        ) {
          return;
        }
        // A store shared with the user was already analyzed when its owner
        // linked it and the owner pays for it: no candidate claims, no billing.
        const dialogRef = this.onboardingFlow.openMoment(
          stage === "delegated-ready"
            ? "delegatedWelcome"
            : "afterCompleteDeliveryAndThresholdForm",
          this.route.snapshot,
        );
        if (stage === "delegated-ready") {
          // The welcome closes onboarding: reload so the KPIs it was holding back appear.
          dialogRef.afterClosed().subscribe((closedOnboarding) => {
            if (closedOnboarding) {
              this.getDashboardData().subscribe();
            }
          });
        }
        return;
      }
      default:
        return;
    }
  }

  private applyStoreProviderFilterState(state: StoreProviderFilterState): void {
    this.providerOptions = state.providerOptions;
    this.filteredStores = state.storesForProvider;
    this.showProviderFilter = state.showProviderFilter;
    this.showStoreFilter = state.showStoreFilter;

    if (!this.filterForm) {
      return;
    }

    this.filterForm.patchValue(
      {
        provider: state.provider,
        store: state.storeId,
      },
      { emitEvent: false },
    );
  }

  private syncProviderStoreFilters(): void {
    if (!this.filterForm) {
      return;
    }

    this.applyStoreProviderFilterState(
      StoreProviderFilterHelper.resolveStoreProviderFilterState(
        this.providers,
        this.stores,
        this.filterForm.value.provider ?? "",
        this.filterForm.value.store ?? "",
        this.storeProviderFilterOptions,
      ),
    );
  }

  private buildInitialFilterState(): {
    store: string;
    provider: string;
  } {
    const state = StoreProviderFilterHelper.resolveStoreProviderFilterState(
      this.providers,
      this.stores,
      undefined,
      undefined,
      this.storeProviderFilterOptions,
    );

    this.applyStoreProviderFilterState(state);

    return {
      store: state.storeId,
      provider: state.provider,
    };
  }

  private setRangeLabelByPeriod(
    startDate: string | null,
    endDate: string | null,
  ): void {
    const match = this.rangeDateOptions.find((option) => {
      if (!option.value) {
        return false;
      }
      return (
        option.value.startDate === startDate && option.value.endDate === endDate
      );
    });

    if (match) {
      this.currentRangeKey = match.displayOption;
      this.labelLastNumberMonths = this.translate.instant(
        `dashboard.${match.displayOption}`,
      );
      this.syncWidgetKpis();
      return;
    }

    if (startDate && endDate) {
      this.currentRangeKey = "Custom Range";
      const translatedCustom = this.translate.instant("dashboard.Custom Range");
      this.labelLastNumberMonths = translatedCustom || "Custom Range";
      this.syncWidgetKpis();
      return;
    }

    this.currentRangeKey = "";
    this.labelLastNumberMonths = "";
    this.syncWidgetKpis();
  }

  onDateRangeChange(
    value: { startDate: string | null; endDate: string | null },
    startKey: string,
    endKey: string,
  ): void {
    if (!this.filterForm) {
      return;
    }

    if (value.startDate && value.endDate) {
      this.filterForm.patchValue({
        [startKey]: value.startDate,
        [endKey]: value.endDate,
      });
      this.setRangeLabelByPeriod(value.startDate, value.endDate);
    } else {
      this.filterForm.patchValue({
        [startKey]: null,
        [endKey]: null,
      });
      this.currentRangeKey = "";
      this.labelLastNumberMonths = "";
      this.syncWidgetKpis();
    }
  }

  clearFilters(): void {
    this.filterForm.patchValue(this.getDefaultFilterValues(), {
      emitEvent: true,
    });
    this.setRangeLabelByPeriod(
      this.filterForm.value.startDate,
      this.filterForm.value.endDate,
    );
  }

  private getDefaultFilterValues(): DashboardFilterFormValue {
    const state = StoreProviderFilterHelper.resolveStoreProviderFilterState(
      this.providers,
      this.stores,
      undefined,
      undefined,
      this.storeProviderFilterOptions,
    );

    return {
      ...this.filterFormInitialStateDefault,
      provider: state.provider,
      store: state.storeId,
    };
  }

  private updateFilterModifiedState(): void {
    if (!this.filterForm) {
      return;
    }

    const currentValues = this.filterForm.value as DashboardFilterFormValue;
    const defaultValues = this.getDefaultFilterValues();
    const isModified = (
      Object.keys(currentValues) as (keyof DashboardFilterFormValue)[]
    ).some((key) => (currentValues[key] ?? "") !== (defaultValues[key] ?? ""));

    this.isFilterModified.set(isModified);
  }

  private buildDashboardAlternativeContentConfig(): AlternativeContentConfig {
    const hasInvitations = this.pendingInvitationsCount() > 0;
    return {
      imageSrc: ORNAMENT_ASSETS.dashboardAlternative,
      imageAlt: "dashboard.no_data.alt",
      title: "dashboard.no_data.title",
      description: hasInvitations
        ? "dashboard.no_data.descriptionWithInvitations"
        : "dashboard.no_data.description",
      actions: [
        {
          label: "dashboard.addAction",
          icon: "link",
          bgBtn: "primary",
          actionType: "dropdown",
          children: this.buildProviderActionsForDashboard(),
        },
        {
          label: "dashboard.learnHowItWorks",
          icon: "help-circle",
          bgBtn: "ghost",
          actionCallback: () => {
            this.tourStoreLinkService.start();
          },
        },
      ],
    };
  }

  private buildProviderActionsForDashboard(): AlternativeContentActionChildConfig[] {
    return this.providers.map((provider) => ({
      label: provider.name,
      showProviderLogo: true,
      providerValue: provider.value,
      actionCallback: () => this.addStoreFromDashboard(provider.value),
    }));
  }

  private addStoreFromDashboard(provider: string): void {
    recordApiRequestStarted(
      (event, description, options) => this.recordSupportHistory(event, description, options),
      `Initiating store link for provider=${provider}`,
    );
    this.storeSvc.initLinkStore(provider).subscribe({
      next: (res) => {
        recordApiRequestSucceeded(
          (event, description, options) => this.recordSupportHistory(event, description, options),
          `Store link initiated for provider=${provider}`,
        );
        this.storeSvc.openStoreLinkReturnUrl(res.returnUrl);
      },
      error: (error) => {
        recordApiRequestFailed(
          (event, description, options) => this.recordSupportHistory(event, description, options),
          "initiate store link",
          error,
          "fetch",
          { provider },
        );
      },
    });
  }

  private buildStoresIncompleteAlternativeContentConfig(): AlternativeContentConfig {
    return {
      imageSrc: ORNAMENT_ASSETS.dashboardAlternative,
      imageAlt: "dashboard.storesIncomplete.imageAlt",
      title: "dashboard.storesIncomplete.title",
      description: "dashboard.storesIncomplete.description",
      actions: [
        {
          label: "dashboard.storesIncomplete.goToStoresAction",
          icon: "arrow-right",
          bgBtn: "primary",
          actionCallback: () => {
            this.router.navigate(["/store/stores"]);
          },
        },
      ],
    };
  }

  private buildNoStoresButOnboardingCompleteAlternativeContentConfig(): AlternativeContentConfig {
    return {
      imageSrc: ORNAMENT_ASSETS.dashboardAlternative,
      imageAlt: "dashboard.noStoresButOnboardingComplete.imageAlt",
      title: "dashboard.noStoresButOnboardingComplete.title",
      description: "dashboard.noStoresButOnboardingComplete.description",
      actions: [
        {
          label: "dashboard.noStoresButOnboardingComplete.addStoreAction",
          icon: "plus",
          bgBtn: "primary",
          actionCallback: () => {
            this.router.navigate(["/store/stores"]);
          },
        },
      ],
    };
  }

  private buildStoresCompleteButOnboardingIncompleteAlternativeContentConfig(): AlternativeContentConfig {
    return {
      imageSrc: ORNAMENT_ASSETS.dashboardAlternative,
      imageAlt: "dashboard.storesCompleteButOnboardingIncomplete.imageAlt",
      title: "dashboard.storesCompleteButOnboardingIncomplete.title",
      description:
        "dashboard.storesCompleteButOnboardingIncomplete.description",
      actions: [
        {
          label:
            "dashboard.storesCompleteButOnboardingIncomplete.completeOnboardingAction",
          icon: "arrow-right",
          bgBtn: "primary",
          actionCallback: () => {
            this.handleStoreCompleteOnboardingAction();
          },
        },
      ],
    };
  }

  private handleStoreCompleteOnboardingAction(): void {
    // Only shared stores: nothing to analyze or pay for, so there is no
    // candidate-claims step to go through — just close onboarding.
    if (this.onboardingStage === "delegated-ready") {
      this.onboardingModalService
        .completeOnboardingWhenDelegatedReady(this.route.snapshot)
        .subscribe((profile) => {
          if (profile) {
            this.getDashboardData().subscribe();
          }
        });
      return;
    }

    const storeId = getOnboardingStore(this.stores)?.storeId;

    if (!storeId) {
      this.router.navigate(["/initial-setup/candidate-claims-found"]);
      return;
    }

    // Best-effort prefetch so candidate-claims-found doesn't have to make its
    // own GET right after this navigation — the backend already started the
    // potential-cases job itself when the store got linked.
    recordApiRequestStarted(
      (event, description, options) => this.recordSupportHistory(event, description, options),
      `Prefetching onboarding potential cases for storeId=${storeId}`,
    );

    this.onboardingModalService
      .prefetchOnboardingPotentialCases(storeId)
      .subscribe({
        next: () => {
          recordApiRequestSucceeded(
            (event, description, options) => this.recordSupportHistory(event, description, options),
            `Onboarding potential claims job ensured for storeId=${storeId}`,
          );
          this.router.navigate(["/initial-setup/candidate-claims-found"]);
        },
        error: (error) => {
          console.error("Error requesting onboarding potential claims:", error);
          recordApiRequestFailed(
            (event, description, options) => this.recordSupportHistory(event, description, options),
            "create onboarding potential claims job",
            error,
            "fetch",
            { storeId },
          );
          this.router.navigate(["/initial-setup/candidate-claims-found"]);
        },
      });
  }

  onDashboardInvitationAccepted(_storeId: string): void {
    this.getDashboardData().subscribe();
  }

  ngAfterViewInit() {
    if (!this.error()) {
      this.setRangeLabelByPeriod(
        this.filterForm.value.startDate,
        this.filterForm.value.endDate,
      );
      this.cdr.detectChanges();
    }
    this.recordSupportHistory("view_ready", describeViewReady("Dashboard"));
  }

  private syncWidgetKpis(): void {
    if (
      !this.filedClaim?.filedClaimProviderBreakdownUIModel ||
      !this.unfilledClaim?.unClaimProviderBreakdownUIModel
    ) {
      this.widgetKpis = createEmptyDashboardWidgetKpis(
        this.labelLastNumberMonths,
        this.caseStatuses ?? [],
      );
      return;
    }

    this.widgetKpis = buildDashboardWidgetKpis(
      this.filedClaim,
      this.unfilledClaim,
      {
        rangeLabel: this.labelLastNumberMonths,
        caseStatuses: this.caseStatuses,
        claimTypes: this.claimTypes,
      },
    );
  }

  filterFormHasAnyValues(): boolean {
    return this.filterForm.value.provider || this.filterForm.value.store;
  }

  getDashboardData(params?: DashboardFilter) {
    params = toDashboardApiQueryParams(params || this.initailRange);
    this.loaderService.show();
    const loadDashboardKpis = isUserOnboardingComplete(
      this.profileService.userProfile(),
    );

    const kpiRequests$ = loadDashboardKpis
      ? {
          filedClaim: this.providerCaseStatusSvc
            .getCachedOrLoadByAppliesTo("case")
            .pipe(
              switchMap((caseStatuses) =>
                this.dashboardSvc.getFilledClaim(caseStatuses, params),
              ),
            ),
          unfiledClaim: this.dashboardSvc.getUnfilledClaim(params),
        }
      : {
          filedClaim: of(null),
          unfiledClaim: of(null),
        };

    return forkJoin({
      ...kpiRequests$,
      providers: this.providerSvc.getCachedOrLoad(),
      stores: this.storeSvc.getCachedOrLoad(),
      claimTypes: this.providerClaimTypeSvc.getCachedOrLoad(),
      caseStatuses: this.providerCaseStatusSvc.getCachedOrLoadByAppliesTo("case"),
    }).pipe(
      tap((data) => {
        this.filedClaim = data.filedClaim as FiledClaimUiModel;
        this.unfilledClaim = data.unfiledClaim as UnClaimUiModel;
        this.providers = data.providers as ProviderGetDTO[];
        this.stores = data.stores as StoreGetDTO[];
        this.claimTypes = data.claimTypes;
        this.caseStatuses = data.caseStatuses;
        this.syncWidgetKpis();
      }),
      finalize(() => {
        this.loaderService.hide();
      }),
      catchError((error) => {
        console.error("Error fetching dashboard data:", error);
        this.loaderService.hide();
        this.recordSupportHistory(
          "data_fetch_failed",
          describeApiRequestFailed(
            "load dashboard data",
            extractSupportErrorMessage(error),
          ),
          { error },
        );
        return EMPTY;
      }),
    );
  }

  onFilterSubmit() {
    const startDate = new Date(this.filterForm.value.startDate)
      .toISOString()
      .split("T")[0];
    const endDate = new Date(this.filterForm.value.endDate)
      .toISOString()
      .split("T")[0];

    const { provider, storeId } =
      StoreProviderFilterHelper.buildStoreProviderApiParams(
        this.filterForm.value.store ?? "",
        this.filterForm.value.provider ?? "",
        this.stores,
        this.providers,
        this.storeProviderFilterOptions,
      );

    const filterParams: DashboardFilter = {
      startDate: startDate,
      endDate: endDate,
      storeId: storeId ?? "",
      provider,
    };

    this.recordSupportHistory(
      "filter_applied",
      describeFilterApplied("dashboard", [
        filterParams.provider ? `provider=${filterParams.provider}` : null,
        filterParams.storeId ? `storeId=${filterParams.storeId}` : null,
        `dates=${filterParams.startDate}..${filterParams.endDate}`,
      ]),
      { filterParams },
    );

    this.getDashboardData(filterParams)
      .pipe(
        tap(() => {
          this.syncProviderStoreFilters();
          this.persistDashboardRouteState();
          const rowCount =
            this.storeProviderReportWidget?.storeProviderReportGrid?.tableData
              ?.length ?? 0;
          this.recordSupportHistory(
            "list_loaded",
            describeListLoaded("Dashboard", rowCount, "filter"),
            { filterParams },
          );
        }),
        catchError((error) => {
          console.error("Error applying filters:", error);
          this.recordSupportHistory(
            "data_fetch_failed",
            describeApiRequestFailed(
              "load dashboard data",
              extractSupportErrorMessage(error),
              { filters: JSON.stringify(filterParams) },
            ),
            { filterParams, error },
          );
          return EMPTY;
        }),
      )
      .subscribe();
  }

  startLinkStoreTour(): void {
    this.tourStoreLinkService.start();
  }

  onAlternativeContentAction(action: AlternativeContentActionConfig): void {
    // Optional hook if you want to track actions or implement side effects.
    console.debug("Alternative content action clicked", action);
  }

  private resolveDashboardRouteContext(): DashboardContextData | undefined {
    return this.routeStore.getCurrentContext()?.data as
      | DashboardContextData
      | undefined;
  }

  private buildDashboardRouteStateSnapshot(): DashboardContextData {
    return {
      filterForm: this.filterForm?.value as DashboardFilterFormValue,
    };
  }

  private persistDashboardRouteState(): void {
    if (!this.filterForm) {
      return;
    }

    this.routeStore.updateCurrentContext({
      data: this.buildDashboardRouteStateSnapshot(),
    });
  }

  private setupNavigationPersistence(): void {
    this.router.events
      .pipe(
        filter(
          (event): event is NavigationStart => event instanceof NavigationStart,
        ),
        takeUntil(this.destroy$),
      )
      .subscribe(() => {
        if (this.isDashboardRouteActive()) {
          this.persistDashboardRouteState();
        }
      });
  }

  private isDashboardRouteActive(): boolean {
    const path = this.router.url.split("?")[0];
    return path === "/dashboard" || path === "/";
  }
}
